import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

// Modal con el mismo esqueleto que los de Usuarios.js. Va por portal a <body>: dentro
// de .navbar el overlay quedaba atrapado en su contexto de apilamiento (se veía borroso
// y detrás del propio fondo).
function Modal({ title, onClose, children, footer }) {
  return createPortal(
    <div className="nv-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="nv-modal">
        <div className="nv-modal-header">
          <span className="nv-modal-title">{title}</span>
          <button className="nv-modal-close" onClick={onClose}>✕</button>
        </div>
        {children}
        <div className="nv-modal-footer">{footer}</div>
      </div>
    </div>,
    document.body
  );
}

function MisDatosModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({
    nombre_completo: user?.nombre_completo || '',
    email: user?.email || '',
    telefono: user?.telefono || '',
  });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    setErr(''); setBusy(true);
    try {
      const res = await api.patch('/auth/me', form);
      onSaved(res.data);
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'No se pudieron guardar los datos');
    } finally { setBusy(false); }
  };

  return (
    <Modal title="Mis datos" onClose={onClose} footer={<>
      <button className="nv-btn nv-btn-ghost" onClick={onClose}>Cancelar</button>
      <button className="nv-btn nv-btn-primary" onClick={submit} disabled={busy}>
        {busy ? 'Guardando…' : 'Guardar'}
      </button>
    </>}>
      {err && <div className="nv-alert nv-alert-err" style={{ marginBottom: 14 }}>{err}</div>}
      <div className="nv-form-row">
        <div className="nv-form-field">
          <label className="nv-label">Usuario</label>
          <input className="nv-input" value={user?.username || ''} disabled />
        </div>
        <div className="nv-form-field">
          <label className="nv-label">Nombre completo</label>
          <input className="nv-input" value={form.nombre_completo} maxLength={100}
            onChange={e => set('nombre_completo', e.target.value)} placeholder="Juan Pérez" />
        </div>
      </div>
      <div className="nv-form-row">
        <div className="nv-form-field">
          <label className="nv-label">Email</label>
          <input className="nv-input" type="email" value={form.email} maxLength={180}
            onChange={e => set('email', e.target.value)} placeholder="nombre@netvoice.ec" />
        </div>
        <div className="nv-form-field">
          <label className="nv-label">Teléfono</label>
          <input className="nv-input" value={form.telefono} maxLength={20}
            onChange={e => set('telefono', e.target.value)} placeholder="0991234567" />
        </div>
      </div>
    </Modal>
  );
}

function PasswordModal({ onClose }) {
  const [current, setCurrent] = useState('');
  const [nueva, setNueva] = useState('');
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState('');
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setErr('');
    // Mismo mínimo que el backend (/auth/change-password exige 10).
    if (!current || !nueva || !confirm) return setErr('Todos los campos son requeridos');
    if (nueva !== confirm) return setErr('Las contraseñas no coinciden');
    if (nueva.length < 10) return setErr('Mínimo 10 caracteres');
    if (current === nueva) return setErr('La nueva debe ser diferente a la actual');
    setBusy(true);
    try {
      await api.post('/auth/change-password', { current_password: current, new_password: nueva });
      setOk(true);
      setCurrent(''); setNueva(''); setConfirm('');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Error al cambiar la contraseña');
    } finally { setBusy(false); }
  };

  return (
    <Modal title="Cambiar contraseña" onClose={onClose} footer={ok
      ? <button className="nv-btn nv-btn-primary" onClick={onClose}>Cerrar</button>
      : <>
          <button className="nv-btn nv-btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="nv-btn nv-btn-primary" onClick={submit} disabled={busy}>
            {busy ? 'Guardando…' : 'Cambiar contraseña'}
          </button>
        </>}>
      {err && <div className="nv-alert nv-alert-err" style={{ marginBottom: 14 }}>{err}</div>}
      {ok && <div className="nv-alert nv-alert-ok" style={{ marginBottom: 14 }}>Contraseña actualizada correctamente</div>}
      {!ok && (
        <div className="nv-form-row" style={{ gridTemplateColumns: '1fr' }}>
          <div className="nv-form-field">
            <label className="nv-label">Contraseña actual</label>
            <input className="nv-input" type="password" autoComplete="current-password"
              value={current} onChange={e => setCurrent(e.target.value)} />
          </div>
          <div className="nv-form-field">
            <label className="nv-label">Nueva contraseña (mínimo 10)</label>
            <input className="nv-input" type="password" autoComplete="new-password"
              value={nueva} onChange={e => setNueva(e.target.value)} />
          </div>
          <div className="nv-form-field">
            <label className="nv-label">Repetir nueva contraseña</label>
            <input className="nv-input" type="password" autoComplete="new-password"
              value={confirm} onChange={e => setConfirm(e.target.value)} />
          </div>
        </div>
      )}
    </Modal>
  );
}

export default function Navbar() {
  const { user, setUser, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState(null); // 'datos' | 'password' | null
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  const abrir = m => { setOpen(false); setModal(m); };
  const faltanDatos = user && !(user.nombre_completo && user.email && user.telefono);

  return (
    <nav className="navbar">
      <div />
      <div className="navbar-right">
        <div className="user-menu" ref={ref}>
          <button className="user-menu-btn" aria-haspopup="menu" aria-expanded={open}
            onClick={() => setOpen(o => !o)}>
            <span className="navbar-user">{user?.username}</span>
            {user?.role === 'admin' && <span className="badge-role">admin</span>}
            <span className="user-menu-caret">▼</span>
          </button>
          {open && (
            <div className="user-menu-list" role="menu">
              <div className="user-menu-head">
                <div className="user-menu-name">{user?.nombre_completo || user?.username}</div>
                <div className="user-menu-sub">{user?.email || 'Sin email registrado'}</div>
              </div>
              <button className="user-menu-item" role="menuitem" onClick={() => abrir('datos')}>
                {faltanDatos ? 'Completar mis datos' : 'Mis datos'}
              </button>
              <button className="user-menu-item" role="menuitem" onClick={() => abrir('password')}>
                Cambiar contraseña
              </button>
              <button className="user-menu-item" role="menuitem" onClick={() => { toggle(); setOpen(false); }}>
                {theme === 'color' ? 'Cambiar a modo oscuro' : 'Cambiar a modo claro'}
              </button>
            </div>
          )}
        </div>
        <button className="btn-logout" onClick={logout}>Salir</button>
      </div>
      {modal === 'datos' && (
        <MisDatosModal user={user} onClose={() => setModal(null)}
          onSaved={perfil => setUser(u => ({ ...u, ...perfil }))} />
      )}
      {modal === 'password' && <PasswordModal onClose={() => setModal(null)} />}
    </nav>
  );
}
